"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useSliderPlayer } from "@/components/SliderPlayerContext";
import TextGenerateEffect from "@/components/TextGenerateEffect";

// Fallback video IDs used if the /api/youtube endpoint fails or no key is
// configured yet. These match Burak's earlier hand-picked list and keep the
// slider functional in local dev without the API key.
const FALLBACK_SLIDES = [
  { videoId: "NC9sUSKtRPg", isShort: false },
  { videoId: "9AfKmkTj5oU", isShort: false },
  { videoId: "hvC_Aw94cG8", isShort: false, start: 1 },
  { videoId: "pvoOmSrMZxk", isShort: false, start: 1 },
  { videoId: "gtsvyNSoGD4", isShort: false },
];

const AUTOSLIDE_MS = 10000;

// Load the YouTube IFrame API exactly once per page.
let apiLoadingPromise = null;
function ensureYouTubeAPI() {
  if (typeof window === "undefined") return Promise.reject();
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (apiLoadingPromise) return apiLoadingPromise;
  apiLoadingPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve();
    };
    if (!document.querySelector("script[data-yt-iframe-api]")) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.dataset.ytIframeApi = "true";
      document.head.appendChild(script);
    }
  });
  return apiLoadingPromise;
}

function YouTubeSlide({ slide, index, onPlayerReady, onStateChange }) {
  const wrapRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    // CRITICAL: create the YT target imperatively so React never tracks
    // it as a child. When YT.Player swallows the target and replaces it
    // with an iframe, React's reconciler knows nothing about it — so
    // the homepage unmounting on navigation doesn't blow up with
    // "removeChild: node is not a child of this node". React only
    // owns the outer wrap ref; the browser handles everything inside
    // it as a unit when the wrap is removed.
    const target = document.createElement("div");
    wrap.appendChild(target);

    let cancelled = false;
    ensureYouTubeAPI().then(() => {
      if (cancelled) return;
      if (!window.YT || !window.YT.Player) return;
      const player = new window.YT.Player(target, {
        videoId: slide.videoId,
        playerVars: {
          autoplay: 0,
          controls: 0,
          mute: 1,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          disablekb: 1,
          fs: 0,
          start: slide.start || 0,
        },
        events: {
          onReady: () => {
            if (cancelled) return;
            playerRef.current = player;
            // Pass the slide's index back explicitly. We used to parse
            // it out of player.getIframe().id with a regex, but that
            // depended on React rendering the target <div id="hl-yt-N">
            // — since the YT target is now created imperatively (so
            // YT.Player doesn't swallow a React-tracked node), the
            // iframe has no id, the regex never matched, and no player
            // was ever registered. Result: no video played at all.
            onPlayerReady(player, index);
          },
          onStateChange: (e) => {
            if (cancelled) return;
            onStateChange(e.data, index);
          },
        },
      });
    });

    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy?.();
      } catch {}
      // Clean anything YT might have left in the wrap (the replaced
      // iframe, the original target div, etc.) before React unmounts
      // the wrap itself — belt-and-suspenders.
      try {
        while (wrap.firstChild) wrap.removeChild(wrap.firstChild);
      } catch {}
    };
    // onPlayerReady / onStateChange are intentionally OMITTED from the
    // dep array — stabilized via useCallback in the parent, and we
    // don't want tree churn to tear down + recreate the YT player.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide.videoId, slide.start, index]);

  return <div className="hl-slide-video" ref={wrapRef} />;
}

export default function HighlightSlider() {
  const trackRef = useRef(null);
  const playersRef = useRef(new Map());
  const ctx = useSliderPlayer();
  const {
    setSlides,
    activeIndex,
    setActiveIndex,
    playing,
    setPlaying,
    muted,
    setMuted,
    setProgress,
    registerControls,
    goTo,
  } = ctx;

  // Latest muted state — read live from the registered toggleMute
  // callback without having to re-register on every state change.
  const mutedRef = useRef(muted);
  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  const [ready, setReady] = useState(false);
  const [videoList, setVideoList] = useState(FALLBACK_SLIDES);

  // Mirror activeIndex in a ref so the callbacks registered with the
  // context always read the latest value without needing to re-register.
  const activeIndexRef = useRef(activeIndex);
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  // Pull the main channel's videos + shorts from the existing API route.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/youtube")
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const videos = Array.isArray(data?.videos) ? data.videos : [];
        const shorts = Array.isArray(data?.shorts) ? data.shorts : [];
        const merged = [...videos, ...shorts]
          .map((v) => ({
            videoId: v.id,
            isShort: !!v.isShort,
            title: v.title,
          }))
          .filter((v) => v.videoId);
        if (merged.length) setVideoList(merged.slice(0, 10));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const slides = videoList;

  useEffect(() => {
    setSlides(slides);
  }, [slides, setSlides]);

  // Register transport controls ONCE. Handlers read the live active
  // index and muted state via refs so we don't re-register on every
  // state change (which would thrash the dock's mounted state).
  useEffect(() => {
    const unregister = registerControls({
      togglePlay: () => {
        const idx = activeIndexRef.current;
        const player = playersRef.current.get(idx);
        if (!player || !player.getPlayerState) return;
        const state = player.getPlayerState();
        if (state === 1) {
          player.pauseVideo();
        } else {
          try {
            player.unMute?.();
          } catch {}
          player.playVideo();
        }
      },
      toggleMute: () => {
        const idx = activeIndexRef.current;
        const player = playersRef.current.get(idx);
        if (!player) return;
        const willUnmute = mutedRef.current;
        try {
          if (willUnmute) {
            player.unMute?.();
            // iOS needs the user-gesture + playVideo pairing to fully
            // unlock audio. The tap on the dock button is the gesture.
            player.playVideo?.();
          } else {
            player.mute?.();
          }
        } catch {}
        setMuted(!willUnmute);
      },
      goTo: (i) => {
        const track = trackRef.current;
        if (!track) return;
        const slide = track.children[i];
        if (!slide) return;
        const offset =
          slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;
        track.scrollTo({ left: offset, behavior: "smooth" });
      },
    });
    return unregister;
  }, [registerControls, setMuted]);

  // Aggressive autoplay recovery for the active slide. Each time the
  // active index changes OR more players become ready, retry
  // playVideo every 400ms for 5s — only calling it when the player
  // actually isn't playing (state !== 1) to avoid fighting YouTube's
  // loop. Stops as soon as the player confirms it's playing.
  useEffect(() => {
    if (!ready) return undefined;
    const player = playersRef.current.get(activeIndex);
    if (!player) return undefined;

    const tryPlay = () => {
      try {
        const state = player.getPlayerState?.();
        // State 1 = playing. Anything else (paused, buffering, cued, -1)
        // means we should try to kick it.
        if (state !== 1) player.playVideo?.();
      } catch {}
    };

    tryPlay();
    const interval = setInterval(tryPlay, 400);
    const stopAt = setTimeout(() => clearInterval(interval), 5000);
    return () => {
      clearInterval(interval);
      clearTimeout(stopAt);
    };
  }, [activeIndex, ready]);

  // First user gesture unlocks autoplay on iOS — kick the active
  // player the moment the user interacts anywhere on the page.
  useEffect(() => {
    let fired = false;
    const kick = () => {
      if (fired) return;
      fired = true;
      const player = playersRef.current.get(activeIndexRef.current);
      try {
        player?.playVideo?.();
      } catch {}
    };
    const opts = { once: true, passive: true };
    window.addEventListener("touchstart", kick, opts);
    window.addEventListener("click", kick, opts);
    window.addEventListener("scroll", kick, opts);
    window.addEventListener("bo:preloader-done", kick);
    return () => {
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("click", kick);
      window.removeEventListener("scroll", kick);
      window.removeEventListener("bo:preloader-done", kick);
    };
  }, []);

  // Active-slide detection via scroll.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let minDist = Infinity;
        Array.from(track.children).forEach((child, i) => {
          const childCenter = child.offsetLeft + child.offsetWidth / 2;
          const dist = Math.abs(childCenter - center);
          if (dist < minDist) {
            minDist = dist;
            closest = i;
          }
        });
        setActiveIndex(closest);
      });
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      track.removeEventListener("scroll", onScroll);
    };
  }, [setActiveIndex]);

  // Play active slide, pause the rest.
  useEffect(() => {
    playersRef.current.forEach((player, i) => {
      if (!player || !player.getPlayerState) return;
      try {
        if (i === activeIndex) player.playVideo();
        else player.pauseVideo();
      } catch {}
    });
  }, [activeIndex, ready]);

  // Progress polling for the active player.
  useEffect(() => {
    const id = setInterval(() => {
      const player = playersRef.current.get(activeIndex);
      if (!player || !player.getCurrentTime || !player.getDuration) return;
      const time = player.getCurrentTime();
      const dur = player.getDuration();
      if (dur > 0) setProgress(Math.max(0, Math.min(1, time / dur)));
    }, 200);
    return () => clearInterval(id);
  }, [activeIndex, setProgress]);

  // Autoslide every 10s when nothing is playing — paused/idle state only.
  useEffect(() => {
    if (!slides.length) return;
    if (playing) return;
    const id = setTimeout(() => {
      goTo((activeIndex + 1) % slides.length);
    }, AUTOSLIDE_MS);
    return () => clearTimeout(id);
  }, [activeIndex, playing, slides.length, goTo]);

  // Stable callback refs for YouTubeSlide so the child's player isn't
  // torn down every render. `idx` now comes in directly from the child
  // instead of being parsed out of player.getIframe().id.
  const handlePlayerReady = useCallback(
    (player, idx) => {
      if (typeof idx !== "number") return;
      playersRef.current.set(idx, player);
      setReady(true);
      if (idx === activeIndexRef.current) {
        try {
          player.playVideo();
        } catch {}
      }
    },
    []
  );

  const handleStateChange = useCallback(
    (state, index) => {
      if (index !== activeIndexRef.current) return;
      if (state === 1) setPlaying(true);
      else if (state === 2) setPlaying(false);
      else if (state === 0) {
        setPlaying(false);
        goTo((activeIndexRef.current + 1) % Math.max(1, slides.length));
      }
    },
    [setPlaying, goTo, slides.length]
  );

  return (
    <section id="highlights" className="hl-section">
      <div className="hl-header">
        <TextGenerateEffect
          as="h2"
          className="hl-title"
          words="Get the highlights."
          duration={0.9}
          staggerDelay={0.09}
        />
      </div>

      <div
        ref={trackRef}
        className="hl-track"
        role="region"
        aria-roledescription="carousel"
        aria-label="Highlights"
      >
        {slides.map((slide, i) => (
          <article
            key={`${slide.videoId}-${i}`}
            className={`hl-slide${slide.isShort ? " hl-slide--short" : " hl-slide--wide"}`}
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${slides.length}`}
          >
            <YouTubeSlide
              slide={slide}
              index={i}
              onPlayerReady={handlePlayerReady}
              onStateChange={handleStateChange}
            />
          </article>
        ))}
      </div>
    </section>
  );
}
